import { useState, useEffect } from 'react'
import { 
  PlusIcon, 
  TrophyIcon, 
  EyeIcon, 
  CheckIcon, 
  XMarkIcon,
  DocumentTextIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import UserAvatar from '../../components/UserAvatar'

interface Achievement {
  id: string
  student: {
    id: string
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  teacher: {
    id: string
    title: string
    user: {
      firstName: string
      lastName: string
    }
  }
  course: {
    id: string
    title: string
    subject: string
  }
  achievementType: 'exam' | 'competition' | 'certification' | 'grade_improvement' | 'other'
  title: string
  description?: string
  score?: number
  maxScore?: number
  grade?: string
  rank?: number
  totalParticipants?: number
  certificateUrl?: string
  achievementDate: string
  academicYear: number
  semester: string
  verificationStatus: 'pending' | 'verified' | 'rejected'
  createdAt: string
}

interface Teacher {
  id: string
  title: string
  user: {
    firstName: string
    lastName: string
  }
}

interface Course {
  id: string
  title: string
  subject: string
  teacher: {
    id: string
    title: string
    user: {
      firstName: string
      lastName: string
    }
  }
}

interface Student {
  id: string
  firstName: string
  lastName: string
  email: string
  avatar?: string
}

const AdminAchievements = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'verified' | 'rejected'>('all')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })

  const [formData, setFormData] = useState({
    studentId: '',
    teacherId: '',
    courseId: '',
    achievementType: 'exam' as Achievement['achievementType'],
    title: '',
    description: '',
    score: '',
    maxScore: '',
    grade: '',
    rank: '',
    totalParticipants: '',
    achievementDate: new Date().toISOString().split('T')[0],
    academicYear: new Date().getFullYear(),
    semester: 'spring' as 'spring' | 'summer' | 'fall' | 'winter' | 'annual',
    notes: ''
  })

  useEffect(() => {
    loadStaticData()
  }, [])

  useEffect(() => {
    loadAchievements()
  }, [activeTab, pagination.page])

  const loadStaticData = async () => {
    try {
      const [teachersRes, coursesRes, studentsRes] = await Promise.all([
        fetch('/api/teachers', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch('/api/admin/courses', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        fetch('/api/admin/users?role=student', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      ])

      if (teachersRes.ok) {
        const teachersData = await teachersRes.json()
        setTeachers(teachersData)
      }

      if (coursesRes.ok) {
        const coursesData = await coursesRes.json()
        setCourses(coursesData)
      }

      if (studentsRes.ok) {
        const studentsData = await studentsRes.json()
        setStudents(studentsData)
      }
    } catch (error) {
      console.error('加载静态数据失败:', error)
    }
  }

  const loadAchievements = async () => {
    try {
      setLoading(true)
      const status = activeTab === 'all' ? 'all' : activeTab
      const response = await fetch(`/api/achievements/all?status=${status}&page=${pagination.page}&limit=${pagination.limit}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAchievements(data.achievements)
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          pages: data.pagination.pages
        }))
      }
    } catch (error) {
      console.error('加载成绩数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      const response = await fetch('/api/achievements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        alert('学生成绩添加成功')
        setShowAddModal(false)
        resetForm()
        loadAchievements()
      } else {
        const error = await response.json()
        alert(`添加失败: ${error.message}`)
      }
    } catch (error) {
      console.error('添加成绩失败:', error)
      alert('添加失败，请重试')
    }
  }

  const handleVerify = async (achievementId: string, status: 'verified' | 'rejected', notes?: string) => {
    try {
      const response = await fetch(`/api/achievements/${achievementId}/verify`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status,
          verificationNotes: notes
        })
      })

      if (response.ok) {
        alert('验证完成')
        loadAchievements()
      } else {
        const error = await response.json()
        alert(`验证失败: ${error.message}`)
      }
    } catch (error) {
      console.error('验证失败:', error)
      alert('验证失败，请重试')
    }
  }

  const resetForm = () => {
    setFormData({
      studentId: '',
      teacherId: '',
      courseId: '',
      achievementType: 'exam',
      title: '',
      description: '',
      score: '',
      maxScore: '',
      grade: '',
      rank: '',
      totalParticipants: '',
      achievementDate: new Date().toISOString().split('T')[0],
      academicYear: new Date().getFullYear(),
      semester: 'spring',
      notes: ''
    })
  }

  const getAchievementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      exam: '考试',
      competition: '竞赛',
      certification: '认证',
      grade_improvement: '成绩提升',
      other: '其他'
    }
    return labels[type] || type
  }

  const getSemesterLabel = (semester: string) => {
    const labels: Record<string, string> = {
      spring: '春季',
      summer: '夏季',
      fall: '秋季',
      winter: '冬季',
      annual: '全年'
    }
    return labels[semester] || semester
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '待验证',
      verified: '已验证',
      rejected: '已拒绝'
    }
    return labels[status] || status
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题和操作 */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">学生成绩管理</h1>
          <p className="mt-2 text-sm text-gray-700">管理和验证学生的学习成果</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            添加学生成绩
          </button>
        </div>
      </div>

      {/* 状态选项卡 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { key: 'all', label: '全部成绩', count: pagination.total },
            { key: 'verified', label: '已验证' },
            { key: 'pending', label: '待验证' },
            { key: 'rejected', label: '已拒绝' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key as any)
                setPagination(prev => ({ ...prev, page: 1 }))
              }}
              className={`${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* 成绩列表 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {achievements.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {achievements.map((achievement) => (
              <li key={achievement.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <UserAvatar
                        user={achievement.student}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {achievement.student.firstName} {achievement.student.lastName}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(achievement.verificationStatus)}`}>
                            {getStatusLabel(achievement.verificationStatus)}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {getAchievementTypeLabel(achievement.achievementType)}
                          </span>
                        </div>
                        <div className="mt-1">
                          <p className="text-sm text-gray-900 font-medium">{achievement.title}</p>
                          <p className="text-sm text-gray-500">
                            课程: {achievement.course.title} ({achievement.course.subject})
                          </p>
                          <p className="text-sm text-gray-500">
                            教师: {achievement.teacher.user.firstName} {achievement.teacher.user.lastName}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            <span>{new Date(achievement.achievementDate).toLocaleDateString('zh-CN')}</span>
                            <span>{achievement.academicYear}学年</span>
                            <span>{getSemesterLabel(achievement.semester)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {achievement.score && (
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            {achievement.score}{achievement.maxScore && `/${achievement.maxScore}`}
                          </div>
                        </div>
                      )}
                      {achievement.grade && (
                        <div className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
                          {achievement.grade}
                        </div>
                      )}
                      <div className="flex space-x-1">
                        <button
                          onClick={() => {
                            setSelectedAchievement(achievement)
                            setShowDetailModal(true)
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                          title="查看详情"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {achievement.verificationStatus === 'pending' && (
                          <>
                            <button
                              onClick={() => handleVerify(achievement.id, 'verified')}
                              className="p-2 text-green-400 hover:text-green-600 hover:bg-green-50 rounded-md"
                              title="验证通过"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const notes = prompt('请输入拒绝原因:')
                                if (notes !== null) {
                                  handleVerify(achievement.id, 'rejected', notes)
                                }
                              }}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                              title="拒绝验证"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">暂无成绩记录</h3>
            <p className="mt-1 text-sm text-gray-500">开始添加学生的学习成果吧</p>
          </div>
        )}
      </div>

      {/* 分页 */}
      {pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
              disabled={pagination.page === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                显示第 <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> 到{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                条，共 <span className="font-medium">{pagination.total}</span> 条记录
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNumber;
                  if (pagination.pages <= 5) {
                    pageNumber = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNumber = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNumber = pagination.pages - 4 + i;
                  } else {
                    pageNumber = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNumber }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === pageNumber
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 添加成绩模态框 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">添加学生成绩</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 学生选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">学生</label>
                  <select
                    value={formData.studentId}
                    onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">选择学生</option>
                    {students.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.firstName} {student.lastName} ({student.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 教师选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">教师</label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => setFormData({ ...formData, teacherId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">选择教师</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.user.firstName} {teacher.user.lastName} - {teacher.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 课程选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">课程</label>
                  <select
                    value={formData.courseId}
                    onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">选择课程</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title} ({course.subject})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 成绩类型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">成绩类型</label>
                  <select
                    value={formData.achievementType}
                    onChange={(e) => setFormData({ ...formData, achievementType: e.target.value as Achievement['achievementType'] })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="exam">考试</option>
                    <option value="competition">竞赛</option>
                    <option value="certification">认证</option>
                    <option value="grade_improvement">成绩提升</option>
                    <option value="other">其他</option>
                  </select>
                </div>
              </div>

              {/* 成绩标题 */}
              <div>
                <label className="block text-sm font-medium text-gray-700">成绩标题</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="如：期末考试、数学竞赛等"
                  required
                />
              </div>

              {/* 成绩描述 */}
              <div>
                <label className="block text-sm font-medium text-gray-700">成绩描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="详细描述成绩情况..."
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* 分数 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">分数</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="88.5"
                  />
                </div>

                {/* 满分 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">满分</label>
                  <input
                    type="number"
                    value={formData.maxScore}
                    onChange={(e) => setFormData({ ...formData, maxScore: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="100"
                  />
                </div>

                {/* 等级 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">等级</label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="A+, A, B+等"
                  />
                </div>

                {/* 排名 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">排名</label>
                  <input
                    type="number"
                    value={formData.rank}
                    onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* 总参与人数 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">总参与人数</label>
                  <input
                    type="number"
                    value={formData.totalParticipants}
                    onChange={(e) => setFormData({ ...formData, totalParticipants: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="30"
                  />
                </div>

                {/* 成绩日期 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">成绩日期</label>
                  <input
                    type="date"
                    value={formData.achievementDate}
                    onChange={(e) => setFormData({ ...formData, achievementDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                {/* 学年 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">学年</label>
                  <input
                    type="number"
                    value={formData.academicYear}
                    onChange={(e) => setFormData({ ...formData, academicYear: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="2020"
                    max="2030"
                    required
                  />
                </div>

                {/* 学期 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">学期</label>
                  <select
                    value={formData.semester}
                    onChange={(e) => setFormData({ ...formData, semester: e.target.value as typeof formData.semester })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="spring">春季</option>
                    <option value="summer">夏季</option>
                    <option value="fall">秋季</option>
                    <option value="winter">冬季</option>
                    <option value="annual">全年</option>
                  </select>
                </div>
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium text-gray-700">备注</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="其他需要说明的信息..."
                />
              </div>

              {/* 提交按钮 */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  添加成绩
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 成绩详情模态框 */}
      {showDetailModal && selectedAchievement && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">成绩详情</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <UserAvatar
                  user={selectedAchievement.student}
                  size="md"
                />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {selectedAchievement.student.firstName} {selectedAchievement.student.lastName}
                  </h4>
                  <p className="text-sm text-gray-500">{selectedAchievement.student.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">课程:</span>
                  <p className="text-gray-900">{selectedAchievement.course.title}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">科目:</span>
                  <p className="text-gray-900">{selectedAchievement.course.subject}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">教师:</span>
                  <p className="text-gray-900">
                    {selectedAchievement.teacher.user.firstName} {selectedAchievement.teacher.user.lastName}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">成绩类型:</span>
                  <p className="text-gray-900">{getAchievementTypeLabel(selectedAchievement.achievementType)}</p>
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">成绩标题:</span>
                <p className="text-gray-900 font-medium">{selectedAchievement.title}</p>
              </div>

              {selectedAchievement.description && (
                <div>
                  <span className="text-sm font-medium text-gray-500">描述:</span>
                  <p className="text-gray-900">{selectedAchievement.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {selectedAchievement.score && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">分数:</span>
                    <p className="text-gray-900 font-bold text-lg">
                      {selectedAchievement.score}{selectedAchievement.maxScore && `/${selectedAchievement.maxScore}`}
                    </p>
                  </div>
                )}
                {selectedAchievement.grade && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">等级:</span>
                    <p className="text-gray-900 font-bold">{selectedAchievement.grade}</p>
                  </div>
                )}
                {selectedAchievement.rank && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">排名:</span>
                    <p className="text-gray-900">
                      第{selectedAchievement.rank}名{selectedAchievement.totalParticipants && `/${selectedAchievement.totalParticipants}人`}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">成绩日期:</span>
                  <p className="text-gray-900">{new Date(selectedAchievement.achievementDate).toLocaleDateString('zh-CN')}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">学年:</span>
                  <p className="text-gray-900">{selectedAchievement.academicYear}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">学期:</span>
                  <p className="text-gray-900">{getSemesterLabel(selectedAchievement.semester)}</p>
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">验证状态:</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${getStatusColor(selectedAchievement.verificationStatus)}`}>
                  {getStatusLabel(selectedAchievement.verificationStatus)}
                </span>
              </div>
            </div>

            {selectedAchievement.verificationStatus === 'pending' && (
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => {
                    handleVerify(selectedAchievement.id, 'verified')
                    setShowDetailModal(false)
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  验证通过
                </button>
                <button
                  onClick={() => {
                    const notes = prompt('请输入拒绝原因:')
                    if (notes !== null) {
                      handleVerify(selectedAchievement.id, 'rejected', notes)
                      setShowDetailModal(false)
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  拒绝验证
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminAchievements 